/**
 * Judge.meレビューバリエーション自動表示ハンドラー（jdgm-form / jdgm-form-dynamic 両対応）
 *
 * 目的（この版の変更点）:
 * - Shopify単一バリエーション商品で発生する "Default Title" を
 *   - 送信（保存）時: 空欄として扱う（cf value = ''）
 *   - 表示時: 行ごと非表示（空欄/Default Title を表示しない）
 *
 * 機能:
 * - ログイン済みユーザー: judgeme-order-data の注文履歴から最新バリエーションを自動プリセット
 * - 未ログインユーザー: 商品ページのバリエーションをプルダウンで選択可能（任意）
 * - 「レビュー対象」カスタムフィールド（radio）の value を、購入バリエーション文字列で上書きして送信
 * - レビュー表示時にカスタムフィールドからバリエーションを表示（Default Title/空欄は非表示）
 */

(function() {
  'use strict';

  // 開発環境のみログ出力
  const DEBUG = window.location.hostname.includes('myshopify.com') ||
                window.location.hostname.includes('shopifypreview.com') ||
                window.location.hostname.includes('localhost');

  function debugLog(...args) {
    if (DEBUG) console.log('[JDGM VAR]', ...args);
  }

  function debugError(...args) {
    if (DEBUG) console.error('[JDGM VAR]', ...args);
  }

  debugLog('Script loaded (dynamic+inline forms)');

  // 初期化フラグ
  let initialized = false;
  let scanTimeout = null;

  // -----------------------------
  // Utilities
  // -----------------------------
  function isDefaultTitle(v) {
    return (v || '').trim() === 'Default Title';
  }

  function normalizeVariantValue(v) {
    const t = (v || '').trim();
    return isDefaultTitle(t) ? '' : t;
  }

  function getOrderData() {
    const el = document.getElementById('judgeme-order-data');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      debugError('Failed to parse order data', e);
      return null;
    }
  }

  function getProductVariants() {
    const productDataEl = document.querySelector('[data-product-json]');
    if (productDataEl) {
      try {
        const data = JSON.parse(productDataEl.textContent);
        if (data.product && Array.isArray(data.product.variants)) {
          return data.product.variants;
        }
      } catch (e) {
        debugError('Failed to parse product data', e);
      }
    }
    try {
      if (window.Shopify && window.Shopify.theme && window.Shopify.theme.product) {
        return window.Shopify.theme.product.variants || [];
      }
    } catch (e) {
      debugError('Failed to get product variants from Shopify.theme', e);
    }
    return [];
  }

  function getProductTitle() {
    const productDataEl = document.querySelector('[data-product-json]');
    if (productDataEl) {
      try {
        const data = JSON.parse(productDataEl.textContent);
        if (data.product && data.product.title) {
          return data.product.title;
        }
      } catch (e) {
        debugError('Failed to parse product data', e);
      }
    }
    const titleEl = document.querySelector('h1.ProductMeta__Title, .ProductMeta__Title, h1.product__title, h1');
    if (titleEl) return titleEl.textContent.trim();
    return '商品';
  }

  // 日付パース関数（YYYY.MM.DD形式に対応）
  function parseOrderDate(dateStr) {
    if (!dateStr) return null;
    const parts = String(dateStr).split('.');
    if (parts.length === 3) {
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10);
      if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
        return new Date(year, month, day);
      }
    }
    return new Date(String(dateStr).replace(/\./g, '-'));
  }

  // -----------------------------
  // UI用セレクトの作成（name なし：送信用ではなく見せる用）
  // -----------------------------
  function createVariantSelectField(orderData, productVariants, isLoggedIn) {
    const wrapper = document.createElement('div');
    wrapper.className = 'jdgm-form__field jdgm-form__field--variant-select';
    wrapper.style.marginBottom = '1em';

    const label = document.createElement('label');
    label.className = 'jdgm-form__label';
    label.textContent = 'レビュー対象';
    label.setAttribute('for', 'jdgm-variant-select-display');
    wrapper.appendChild(label);

    const select = document.createElement('select');
    select.id = 'jdgm-variant-select-display';
    select.className = 'jdgm-form__input jdgm-form__select';
    select.setAttribute('data-custom-field', 'variant');
    select.style.width = '100%';
    select.style.padding = '8px';
    select.style.marginTop = '0.5em';

    const hasPurchaseHistory = !!(
      isLoggedIn &&
      orderData &&
      Array.isArray(orderData.variants) &&
      orderData.variants.length > 0
    );

    if (hasPurchaseHistory) {
      // 購入履歴からバリエーションを構築（新しい注文日順）
      const sorted = orderData.variants.slice().sort(function(a, b) {
        const da = parseOrderDate(a.orderDate);
        const db = parseOrderDate(b.orderDate);
        if (!da || !db) return 0;
        return db - da;
      });

      // Default Title を空欄扱いにしつつ、UIには出さない
      const filteredSorted = sorted.filter(function(v) {
        return !isDefaultTitle(v && v.title);
      });

      if (!filteredSorted.length) {
        // 「Default Title しかない」などで空になった場合は空optionを1つ入れて固定
        const empty = document.createElement('option');
        empty.value = '';
        empty.textContent = '';
        select.appendChild(empty);
        select.disabled = true;
        select.style.backgroundColor = '#f5f5f5';
        select.style.cursor = 'not-allowed';
      } else {
        filteredSorted.forEach(function(v) {
          const opt = document.createElement('option');
          const cleanTitle = normalizeVariantValue(v.title);
          const labelText = cleanTitle + '（注文日：' + v.orderDate + '）';
          opt.value = cleanTitle;           // 保存用はタイトル（Default Titleは除外済み）
          opt.textContent = labelText;      // 表示はタイトル + 購入日
          opt.setAttribute('data-variant-id', v.id);
          opt.setAttribute('data-order-date', v.orderDate);
          select.appendChild(opt);
        });

        const uniqueIds = new Set(filteredSorted.map(function(v) { return v.id; }));
        const latestTitle = filteredSorted.length ? normalizeVariantValue(filteredSorted[0].title) : null;

        if (uniqueIds.size === 1 && filteredSorted.length === 1) {
          // 同一バリエーション1回のみ → 変更不可
          select.disabled = true;
          select.style.backgroundColor = '#f5f5f5';
          select.style.cursor = 'not-allowed';
          if (latestTitle) select.value = latestTitle;
        } else if (latestTitle) {
          // 複数回 or 別バリエーションあり → 最新をデフォルト
          select.value = latestTitle;
        }
      }
    } else {
      // 未購入 or 未ログイン → 商品バリエーションから
      const available = productVariants.filter(function(v) { return v && v.available === true; });

      // まず空option（未選択）を入れる（Default Title/単一商品でも空欄でOKにするため）
      const empty = document.createElement('option');
      empty.value = '';
      empty.textContent = '';
      select.appendChild(empty);

      if (!productVariants.length || !available.length) {
        // バリエーション情報が無い/取得できない → 商品名を参考表示（ただし送信値は空にしておく）
        const title = getProductTitle();
        if (title) {
          const opt = document.createElement('option');
          opt.value = '';          // 送信値は空（安全）
          opt.textContent = title; // 表示だけ商品名
          select.appendChild(opt);
        }
      } else {
        available.forEach(function(v) {
          // Default Title はUIから除外
          if (isDefaultTitle(v.title)) return;
          const opt = document.createElement('option');
          opt.value = normalizeVariantValue(v.title);
          opt.textContent = normalizeVariantValue(v.title);
          opt.setAttribute('data-variant-id', v.id);
          select.appendChild(opt);
        });
      }
    }

    wrapper.appendChild(select);
    return wrapper;
  }

  // -----------------------------
  // 新版レビュー投稿フォーム（マルチステップ・モーダル）対応ヘルパー
  // 旧版レイアウト（.jdgm-form / .jdgm-form-dynamic）では下記の判定はすべて
  // 「旧版」側に落ちるため、旧版の処理経路は従来と同一になる。
  // -----------------------------
  const VARIANT_UI_CLASS = 'jdgm-form__field--variant-select';
  const VARIANT_SELECT_ID = 'jdgm-variant-select-display';
  const VARIANT_SELECT_SELECTOR = 'select[data-custom-field="variant"]';
  // 新版モーダルの BEM ブロック名（.jdgm-write-review-modal / __page / __form / __field-group / __chip-input 等）
  const NEW_FORM_BLOCK = 'jdgm-write-review-modal';
  // 新版モーダルは <div> のため submit が発火しない。送信/次へ相当のクリックを拾うためのセレクタ。
  // 実 DOM を確認できないため広めに取り、明らかに送信でないもの（下記）だけを除外する
  const SUBMIT_LIKE_SELECTOR = 'button, input[type="submit"], input[type="button"], ' +
    '[class*="submit"], [class*="next"], [role="button"]';
  const NON_SUBMIT_SELECTOR = '[class*="back"], [class*="prev"], [class*="cancel"], ' +
    '[class*="close"], [class*="dismiss"]';

  // 再バインド（新版モーダルの再レンダリング等）で作り直すときに引き継ぐ、直近の選択値
  let lastVariantSelection = null;

  // 新版モーダルのクラスを持つ要素か。
  // ⚠ class の部分文字列一致では旧版レイアウトを巻き込む恐れがあるため、
  //   クラス「トークン」単位で NEW_FORM_BLOCK 自体か その BEM 要素（block__element）かを判定する。
  //   （例: 'jdgm-write-review-modal-container' のような別ブロック名には一致しない）
  function hasNewFormMarker(el) {
    if (!el || el.nodeType !== 1 || !el.classList) return false;
    for (let i = 0; i < el.classList.length; i++) {
      const token = el.classList[i];
      if (token === NEW_FORM_BLOCK || token.indexOf(NEW_FORM_BLOCK + '__') === 0) return true;
    }
    return false;
  }

  // 新版モーダルの最も外側のコンテナを返す。旧版レイアウトでは常に null
  function getNewFormRoot(el) {
    let root = null;
    let cur = el;
    while (cur && cur.nodeType === 1) {
      if (hasNewFormMarker(cur)) root = cur;
      cur = cur.parentElement;
    }
    return root;
  }

  // 独自 select（UI用）から wrapper を辿って DOM から取り除く。
  // このとき、隠していた質問ラベル／元の radio UI は元に戻す
  // （独自 select が無い状態で隠したままにすると、その質問に回答できなくなるため）
  function removeVariantUi(select) {
    if (select && select.__jdgmVariantFieldset) {
      restoreOriginalCustomFieldUi(select.__jdgmVariantFieldset, select.__jdgmVariantRadio);
    }
    const wrapper = (select && select.closest) ? select.closest('.' + VARIANT_UI_CLASS) : null;
    const node = wrapper || select;
    if (node && node.parentNode) node.parentNode.removeChild(node);
  }

  // 質問ラベルの表示に関わる要素を【それぞれ独立に】集める。
  // ★「header があれば header、無ければ質問ラベル」の一発判定は使わない。
  //   header の生成タイミング（ログイン済みは注文履歴の非同期処理で順序が変わる）に
  //   結果が左右され、ヘルプ文「一つのオプションを選択してください」だけが
  //   画面に残る不具合が起きたため（2026-08-20 実機で3回再現）。
  // 旧版レイアウトには header もヘルプ文も存在しないため、質問ラベルのみが対象になる
  //   ＝従来どおりの挙動。
  // ★隠す側と戻す側で必ず同じ集合になるよう、判定はこの関数に集約する
  function getQuestionLabelElements(fieldset) {
    if (!fieldset) return [];
    const elements = [];
    const header = fieldset.querySelector('.' + NEW_FORM_BLOCK + '__field-group-header');
    if (header) elements.push(header);
    const questionLabel = fieldset.querySelector('[id^="jdgm_question_label_"]');
    if (questionLabel) elements.push(questionLabel);
    const labelHelp = fieldset.querySelector('.' + NEW_FORM_BLOCK + '__field-label-help');
    if (labelHelp) elements.push(labelHelp);
    return elements;
  }

  // 質問ラベル／ヘルプ文を非表示にする。
  // 既に none のものは触らない（毎スキャン呼ばれても余計な書き込みとログを出さないため）
  function hideQuestionLabelUi(fieldset) {
    getQuestionLabelElements(fieldset).forEach(function(el) {
      if (el.style.display === 'none') return;
      el.style.display = 'none';
      debugLog('Hidden question label element:', el.id || el.className);
    });
  }

  // 非表示にしていた質問ラベル／ヘルプ文の表示を戻す（隠した集合と同じ集合を戻す）
  function restoreQuestionLabelUi(fieldset) {
    getQuestionLabelElements(fieldset).forEach(function(el) {
      el.style.display = '';
    });
  }

  // 隠していた質問ラベル／radio UI の表示を戻す（隠すときに触った項目だけを戻す）
  function restoreOriginalCustomFieldUi(fieldset, radio) {
    if (!fieldset) return;
    restoreQuestionLabelUi(fieldset);

    const optionsContainer = fieldset.querySelector('.jdgm-cf__options');
    if (optionsContainer) {
      optionsContainer.style.position = '';
      optionsContainer.style.opacity = '';
      optionsContainer.style.pointerEvents = '';
      optionsContainer.style.height = '';
      optionsContainer.style.overflow = '';
    } else if (radio) {
      radio.style.display = '';
      const radioLabel = fieldset.querySelector('label[for="' + radio.id + '"]');
      if (radioLabel) radioLabel.style.display = '';
    }
  }

  // 指定の fieldset / radio に対して「生きている」バインドがあるか実体で検証する（要件7）
  // - select が document 内に存在する（querySelectorAll で取れる＝接続済み）
  // - その select が、今回見つかった fieldset と radio の“同一インスタンス”に結びついている
  function findLiveBinding(fieldset, radio) {
    const selects = document.querySelectorAll(VARIANT_SELECT_SELECTOR);
    for (let i = 0; i < selects.length; i++) {
      const s = selects[i];
      if (s.__jdgmVariantFieldset === fieldset && s.__jdgmVariantRadio === radio) return s;
    }
    return null;
  }

  // 指定コンテナ内のバインドが「健全」かを実体で検証する（要件7）。
  // 健全＝独自 select がこのコンテナ内にあり、紐づく fieldset もこのコンテナ内、
  //       紐づく radio が DOM 上に生きていて、かつその fieldset の中にある
  function findLiveBindingIn(container) {
    const selects = document.querySelectorAll(VARIANT_SELECT_SELECTOR);
    for (let i = 0; i < selects.length; i++) {
      const s = selects[i];
      const fs = s.__jdgmVariantFieldset;
      const radio = s.__jdgmVariantRadio;
      if (!fs || !radio) continue;
      if (container.contains(s) && container.contains(fs) &&
          document.contains(radio) && fs.contains(radio)) {
        return s;
      }
    }
    return null;
  }

  // 新版モーダルの質問（fieldset 自身が新版のクラスを持つ）に生きているバインドがあるか。
  // ⚠ 判定は「祖先」ではなく fieldset 自身のクラスで行う。旧 widget の残骸が
  //    モーダルの内側に置かれている場合でも、残骸側を新版と誤認しないため
  function hasLiveNewFormBinding() {
    const selects = document.querySelectorAll(VARIANT_SELECT_SELECTOR);
    for (let i = 0; i < selects.length; i++) {
      const fs = selects[i].__jdgmVariantFieldset;
      if (fs && document.contains(fs) && hasNewFormMarker(fs)) return true;
    }
    return false;
  }

  // 旧レイアウトの質問（＝旧 widget 残骸）に作られた UI を取り除く。
  // 新版環境で #jdgm-variant-select-display が複数生成されるのを防ぐためのもので、
  // 旧版レイアウトでは新版の質問が存在しない＝この関数は呼ばれない
  function removeLegacyVariantUi() {
    document.querySelectorAll(VARIANT_SELECT_SELECTOR).forEach(function(s) {
      const fs = s.__jdgmVariantFieldset;
      if (fs && !hasNewFormMarker(fs)) {
        debugLog('Removing legacy-layout variant select (new modal takes precedence)');
        removeVariantUi(s);
      }
    });
  }

  // 作り直しの際、直前にユーザーが選んでいた値を引き継ぐ（同じ値の option がある場合のみ）。
  // disabled（＝仕様で1件に固定される状態）のときは触らない
  function restorePreviousSelection(uiSelect) {
    if (lastVariantSelection === null || !uiSelect || uiSelect.disabled) return false;
    for (let i = 0; i < uiSelect.options.length; i++) {
      if (uiSelect.options[i].value === lastVariantSelection) {
        uiSelect.selectedIndex = i;
        debugLog('Restored previous selection:', lastVariantSelection);
        return true;
      }
    }
    return false;
  }

  // 同じ fieldset に紐づく（＝radio が差し替わって無効になった）UI を取り除く
  function removeVariantUiFor(fieldset) {
    document.querySelectorAll(VARIANT_SELECT_SELECTOR).forEach(function(s) {
      if (s.__jdgmVariantFieldset === fieldset) {
        debugLog('Removing outdated variant select bound to this fieldset');
        removeVariantUi(s);
      }
    });
  }

  // 参照先の radio / fieldset が DOM から消えた孤児 UI を掃除する（重複挿入・死んだ導線の防止）
  function cleanupStaleVariantUi() {
    document.querySelectorAll(VARIANT_SELECT_SELECTOR).forEach(function(s) {
      const radio = s.__jdgmVariantRadio;
      const fieldset = s.__jdgmVariantFieldset;
      if (!radio || !fieldset) return; // 由来が特定できないものは触らない
      if (!document.contains(radio) || !document.contains(fieldset)) {
        debugLog('Cleanup: removing orphaned variant select');
        removeVariantUi(s);
      }
    });
  }

  // UI select の選択値を radio へ反映する（値決定ロジックは現行のまま）
  function applyVariantSelectToRadio(uiSelect, targetRadio) {
    if (!uiSelect || !targetRadio) return;

    const opt = uiSelect.options[uiSelect.selectedIndex];
    if (!opt) {
      // option自体がない異常ケース
      targetRadio.value = '';
      targetRadio.checked = false; // 値がないので false
      debugLog('syncToRadio: no option, set empty');
      return;
    }

    // value属性を優先（注文日付なしのタイトルのみ）
    let value = opt.value;

    // valueが空で、textContentに（注文日：...）がある場合は除去して推定
    // 注文日付が含まれている場合のみ復元（商品タイトル表示オプションの送信を防ぐ）
    if (!value && opt.textContent && opt.textContent.includes('（注文日：')) {
      value = opt.textContent.split('（注文日：')[0].trim();
    }

    value = normalizeVariantValue(value); // Default Title => ''

    targetRadio.value = value;   // 送信される値
    targetRadio.checked = !!value;  // 値がある場合のみ true
    debugLog('syncToRadio =>', value, 'checked:', !!value);
  }

  // 現在 DOM に生きている全バインドを同期する（新版の送信/次へ click フック用）
  function syncLiveVariantSelections() {
    document.querySelectorAll(VARIANT_SELECT_SELECTOR).forEach(function(s) {
      const radio = s.__jdgmVariantRadio;
      if (!radio || !document.contains(radio)) return;
      applyVariantSelectToRadio(s, radio);
      lastVariantSelection = radio.value;
    });
  }

  // -----------------------------
  // 「レビュー対象」カスタムフィールドと UI セレクトを結びつける
  // -----------------------------
  function bindVariantFieldToCustomField(form) {
    if (!form) return;

    try {
      debugLog('bindVariantFieldToCustomField called on form:', form.className);

      // 既存バインドが健全なら、セレクタ探索の前に抜ける（実体で検証するので要件7を満たす）。
      // 入れ子コンテナ（新版の __page と __form が両方ヒットする）もここで吸収される
      const liveSelectInForm = findLiveBindingIn(form);
      if (liveSelectInForm) {
        // バインド後に header / ヘルプ文が生成される順序でも取り残しが出ないよう、
        // 非表示は毎回かけ直す（既に none のものには何もしない）
        hideQuestionLabelUi(liveSelectInForm.__jdgmVariantFieldset);
        if (!form.hasAttribute('data-variant-field-processed')) {
          form.setAttribute('data-variant-field-processed', 'true');
        }
        return;
      }

      let targetFieldset = null;
      let targetRadio = null;

      // form の中から .jdgm-cf を探す
      // 旧版: .jdgm-form__fieldset.jdgm-cf / 新版: .jdgm-write-review-modal__field-group.jdgm-cf
      // → 共通する .jdgm-cf 単独で拾う
      const fieldsets = form.querySelectorAll('.jdgm-cf');
      fieldsets.forEach(function(fs) {
        // 先に見つけた候補を尊重する（＝従来どおり先勝ち）。
        // ただし候補の内側にさらに .jdgm-cf がある入れ子構造では、内側＝実際の質問側を採用する
        if (targetFieldset && !targetFieldset.contains(fs)) return;
        // 質問ラベルは 旧版=label / 新版=div。id 規則（jdgm_question_label_*）は新旧共通なので第一候補にする
        const label = fs.querySelector('[id^="jdgm_question_label_"]') || fs.querySelector('label');
        const text = (label ? label.textContent : fs.textContent).trim();
        if (text.includes('レビュー対象') || text.toLowerCase().includes('review target')) {
          targetFieldset = fs;
          targetRadio = fs.querySelector('input.jdgm-cf-input[type="radio"][name*="[value]"]');
        }
      });

      if (!targetFieldset || !targetRadio) {
        debugLog('No "レビュー対象" custom field found in this form.');
        return;
      }

      debugLog('Found "レビュー対象" custom field', {
        radioName: targetRadio.name,
        radioId: targetRadio.id
      });

      // 新版モーダルかどうか（旧版レイアウトでは null → 従来経路）
      // isNewForm      : この質問が新版モーダルの中にあるか（要件6のスキップ／click フックの範囲に使う）
      // isNewFormField : この質問「自身」が新版の要素か（どちらのバインドを優先するかに使う）
      //                  ＝ 旧 widget 残骸がモーダル内にあっても残骸側は false
      const newFormRoot = getNewFormRoot(targetFieldset);
      const isNewForm = !!newFormRoot;
      const isNewFormField = hasNewFormMarker(targetFieldset);

      // 新版モーダルが導線を持っている間は、旧 widget の残骸側では生成しない。
      // （#jdgm-variant-select-display は id なので、新版環境で複数生成されるのを防ぐ）
      // 旧版レイアウトには新版の質問が存在しないため、この分岐は常に false になる
      if (!isNewFormField && hasLiveNewFormBinding()) {
        debugLog('Skipping legacy container: new modal already owns the variant select.');
        return;
      }

      // 処理済み判定はフラグではなく実体で行う（要件7）
      // 新版モーダルはステップ遷移で再レンダリングされうるため、フラグが残ったまま
      // 中身だけ差し替わったケースを検出して再バインドする
      if (findLiveBinding(targetFieldset, targetRadio)) {
        // 上と同じ理由で、生きているバインドに対しても非表示をかけ直す
        hideQuestionLabelUi(targetFieldset);
        if (!form.hasAttribute('data-variant-field-processed')) {
          form.setAttribute('data-variant-field-processed', 'true');
        }
        debugLog('Variant select is already bound and alive. Skipping.');
        return;
      }
      // この fieldset に紐づく古い UI が残っていれば取り除いてから作り直す（重複挿入の防止）
      removeVariantUiFor(targetFieldset);
      form.removeAttribute('data-variant-field-processed');

      // UI 用のプルダウンを生成
      // （テーマ側データの読み取りは、実際に生成が必要になったこの時点で行う）
      const orderData = getOrderData();
      const productVariants = getProductVariants();
      const isLoggedIn = !!(orderData && orderData.isLoggedIn);

      const uiWrapper = createVariantSelectField(orderData, productVariants, isLoggedIn);
      const uiSelect = uiWrapper.querySelector('select');

      if (!uiSelect) {
        // 通常到達しない（createVariantSelectField は必ず select を生成する）。
        // select が無いと実体検証が成立せず再挿入が積み重なるため、挿入せず打ち切る。
        // 元の radio UI はまだ隠していないので、この場合ユーザーは標準 UI で回答できる
        debugError('Variant select could not be created. Aborting bind.');
        return;
      }

      // 挿入前に同 id（#jdgm-variant-select-display）の既存要素を確認する（要件7）。
      // これから挿入するスコープ（新版はモーダルルート／旧版はこのコンテナ）の中に
      // 生きている同 id 要素があるなら、重複を作らないためここで打ち切る。
      // ⚠ 旧版は .jdgm-form と .jdgm-form-dynamic が別スコープなので、
      //    従来どおり各コンテナに1つずつ生成される（既存挙動を変えない）
      const insertScope = newFormRoot || form;
      const existingById = document.getElementById(VARIANT_SELECT_ID);
      if (existingById && insertScope.contains(existingById)) {
        const existingFieldset = existingById.__jdgmVariantFieldset;
        const existingIsNewFormField = !!(existingFieldset && hasNewFormMarker(existingFieldset));
        if (isNewFormField && !existingIsNewFormField) {
          // 既存が旧 widget 残骸由来（モーダル内に残骸がある場合）なら、新版の質問側を優先する
          debugLog('Replacing legacy variant select found in this scope.');
          removeVariantUi(existingById);
        } else {
          debugLog('Aborting insert: #' + VARIANT_SELECT_ID + ' already exists in this scope.');
          return;
        }
      }

      // 質問ラベルとヘルプ文を非表示にする
      // 新版モーダル: header（掴めた場合）／質問ラベル／ヘルプ文をそれぞれ独立に
      // 旧版レイアウト: 質問ラベル（jdgm_question_label_*）のみ＝従来どおり
      hideQuestionLabelUi(targetFieldset);

      // 元のラジオ UI は隠す（input 自体は残して送信用に使う）
      const optionsContainer = targetFieldset.querySelector('.jdgm-cf__options');
      if (optionsContainer) {
        optionsContainer.style.position = 'absolute';
        optionsContainer.style.opacity = '0';
        optionsContainer.style.pointerEvents = 'none';
        optionsContainer.style.height = '0';
        optionsContainer.style.overflow = 'hidden';
      } else {
        targetRadio.style.display = 'none';
        const radioLabel = targetFieldset.querySelector('label[for="' + targetRadio.id + '"]');
        if (radioLabel) radioLabel.style.display = 'none';
      }

      // UI → radio の同期
      function syncToRadio() {
        applyVariantSelectToRadio(uiSelect, targetRadio);
        lastVariantSelection = targetRadio.value;
      }

      // 実体検証（要件7）用に、この select がどの fieldset / radio に紐づいたかを保持する
      uiSelect.__jdgmVariantFieldset = targetFieldset;
      uiSelect.__jdgmVariantRadio = targetRadio;

      // 作り直しの場合、直前の選択を引き継ぐ（初回バインド時は引き継ぐ値がないので何もしない）
      restorePreviousSelection(uiSelect);

      // 初期同期（デフォルト選択されたものを radio に反映）
      syncToRadio();
      uiSelect.addEventListener('change', syncToRadio);

      // 送信前にも同期（念のため）。再バインド時に積み重ならないよう常に1つだけ登録する
      if (form.__jdgmVariantSubmitHook) {
        form.removeEventListener('submit', form.__jdgmVariantSubmitHook, true);
      }
      const submitHook = function() {
        syncToRadio();
      };
      form.__jdgmVariantSubmitHook = submitHook;
      form.addEventListener('submit', submitHook, true);

      // 新版モーダルは <div> のため submit が発火しない。
      // 送信/次へ相当のクリックをキャプチャフェーズで拾って同期する（旧版の submit フックは上でそのまま維持）
      if (isNewForm) {
        if (newFormRoot.__jdgmVariantClickHook) {
          // 再バインド時に古いリスナが積み重ならないよう、常に1つだけ登録する
          newFormRoot.removeEventListener('click', newFormRoot.__jdgmVariantClickHook, true);
        }
        const clickHook = function(e) {
          const t = e && e.target;
          if (!t || typeof t.closest !== 'function') return;
          const btn = t.closest(SUBMIT_LIKE_SELECTOR);
          if (!btn) return;
          // 戻る・閉じる・キャンセル等、明らかに送信でないものは対象外
          if (btn.matches && btn.matches(NON_SUBMIT_SELECTOR)) return;
          // クロージャの値を使わず、その時点で生きているバインドを解決して同期する
          syncLiveVariantSelections();
        };
        newFormRoot.__jdgmVariantClickHook = clickHook;
        newFormRoot.addEventListener('click', clickHook, true);
        debugLog('Registered new-form click hook on', newFormRoot.className);
      }

      // カスタムフィールド fieldset の直後に UI プルダウンを挿入
      if (targetFieldset.parentNode) {
        targetFieldset.parentNode.insertBefore(uiWrapper, targetFieldset.nextSibling);
      } else {
        form.appendChild(uiWrapper);
      }

      // jdgm-custom-forms を2つ目の jdgm-form__fieldset の次に移動
      // 旧版レイアウト専用の処理。新版モーダルでは構造を壊さないよう明示的にスキップする（要件6）
      if (isNewForm) {
        debugLog('Skipped jdgm-custom-forms relocation (new modal form)');
      } else {
        const customForms = form.querySelector('.jdgm-custom-forms');
        if (customForms) {
          const fsAll = form.querySelectorAll('.jdgm-form__fieldset');
          if (fsAll.length >= 2) {
            const secondFieldset = fsAll[1];
            if (secondFieldset && secondFieldset.nextSibling !== customForms) {
              if (customForms.parentNode) customForms.parentNode.removeChild(customForms);
              if (secondFieldset.parentNode) {
                secondFieldset.parentNode.insertBefore(customForms, secondFieldset.nextSibling);
                debugLog('Moved jdgm-custom-forms after second fieldset');
              }
            }
          }
        }
      }

      // 新版モーダルが導線を持ったら、旧 widget 残骸側に作られていた UI は撤去する（id 重複の解消）
      if (isNewFormField) {
        removeLegacyVariantUi();
      }

      form.setAttribute('data-variant-field-processed', 'true');
    } catch (error) {
      debugError('Error binding variant field:', error);
      // エラー時もフォームが使えるようにする
    }
  }

  // -----------------------------
  // Judge.me のフォーム検出（inline + dynamic 両方）
  // -----------------------------
  function scanAndBindForms() {
    // 参照先が DOM から消えた独自 select を先に掃除する（新版モーダルの再レンダリング対策）
    cleanupStaleVariantUi();

    // 新版モーダルが導線を持っている間は、旧 widget 残骸側に残っている UI を毎回撤去する。
    // モーダルが DOM から外れて戻る（同じ要素が復活する）経路では新規バインドが走らないため、
    // 撤去をバインド内だけに置くと id が重複したまま残ってしまう
    if (hasLiveNewFormBinding()) {
      removeLegacyVariantUi();
    }

    // 旧版レイアウト（form 要素）＋ 新版モーダル（div コンテナ）の両方を検出する。
    // ここで得られる要素は「form 要素」ではなく「フォームのコンテナ要素」なので、
    // form 要素であることを前提にした処理を書かないこと
    const forms = document.querySelectorAll(
      '.jdgm-form, form.jdgm-form, .jdgm-form-dynamic, form.jdgm-form-dynamic, ' +
      '.jdgm-write-review-modal__form, .jdgm-write-review-modal__page'
    );

    debugLog('scanAndBindForms, found', forms.length, 'forms');

    forms.forEach(function(form) {
      bindVariantFieldToCustomField(form);
    });
  }

  function handleJudgeMeForm() {
    // 初回スキャン
    scanAndBindForms();

    // 「レビューを書く」などのボタンがある場合は、それを押した後にもスキャン
    const writeLinks = document.querySelectorAll(
      '.jdgm-write-rev-link, .jdgm-ask-question-btn, [data-open-review-form="true"], a[href*="#judgeme"], ' +
      // 新版ウィジェットの「レビューを書く」ボタン（クラストークンで指定・部分文字列一致は使わない）
      '.jm-action-buttons__button'
    );
    writeLinks.forEach(function(link) {
      link.addEventListener('click', function() {
        setTimeout(scanAndBindForms, 700); // モーダル出現待ち
      });
    });

    // DOM変化を監視して、新しいフォームが出てきたら都度スキャン（デバウンス処理）
    const observer = new MutationObserver(function() {
      if (scanTimeout) clearTimeout(scanTimeout);
      scanTimeout = setTimeout(scanAndBindForms, 100);
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  // -----------------------------
  // レビュー表示側：カスタムフィールドの「レビュー対象」から Default Title/空欄を非表示
  // -----------------------------
  function addVariantToReviews() {
    const reviews = document.querySelectorAll('.jdgm-rev');
    debugLog('addVariantToReviews called, found', reviews.length, 'reviews');

    reviews.forEach(function(review) {
      // ★ ".jdgm-rev__variant" の有無で早期returnしない（Judge.me標準で入ってることがあるため）
      if (review.getAttribute('data-jdgm-variant-processed') === 'true') return;

      const body = review.querySelector('.jdgm-rev__body, .jdgm-rev__text, .jdgm-rev__content');
      if (!body) return;

      // 「レビュー対象」のタイトル要素を直接検索して非表示にする（ラベル自体を消す）
      const titleElements = review.querySelectorAll('.jdgm-rev__cf-ans__title, .jdgm-rev__cf-ans__label');
      titleElements.forEach(function(titleEl) {
        const titleText = (titleEl.textContent || '').trim();
        if (titleText.includes('レビュー対象') || titleText.toLowerCase().includes('review target')) {
          titleEl.style.display = 'none';
        }
      });

      // バリエーション（レビュー対象）行を取得して、Default Title/空欄なら行ごと消す
      const cfAnswers = review.querySelectorAll('.jdgm-rev__cf-ans');
      cfAnswers.forEach(function(ans) {
        const titleEl = ans.querySelector('.jdgm-rev__cf-ans__title, .jdgm-rev__cf-ans__label');
        const valueEl = ans.querySelector('.jdgm-rev__cf-ans__value');
        if (!titleEl || !valueEl) return;

        const titleText = (titleEl.textContent || '').trim();
        if (!(titleText.includes('レビュー対象') || titleText.toLowerCase().includes('review target'))) return;

        const v = (valueEl.textContent || '').trim();
        if (!v || isDefaultTitle(v)) {
          ans.style.display = 'none';
        }
      });

      // 既存のjdgm-rev__variant要素を非表示（Judge.me標準のvariant表示がある場合）
      const existingVariant = review.querySelector('.jdgm-rev__variant');
      if (existingVariant) {
        existingVariant.style.display = 'none';
      }

      // 処理済みフラグ
      review.setAttribute('data-jdgm-variant-processed', 'true');
    });
  }

  function handleReviewDisplay() {
    addVariantToReviews();

    const observer = new MutationObserver(function(mutations) {
      let shouldUpdate = false;
      mutations.forEach(function(mutation) {
        if (mutation.type !== 'childList') return;
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          if (node.classList && node.classList.contains('jdgm-rev')) {
            shouldUpdate = true;
          } else if (node.querySelector && node.querySelector('.jdgm-rev')) {
            shouldUpdate = true;
          }
        });
      });
      if (shouldUpdate) {
        // 追加DOMに対しても適用
        setTimeout(addVariantToReviews, 100);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  // -----------------------------
  // レビュー表示の共通処理（匿名→非公開、お客様の声→REVIEW）
  // -----------------------------
  // ★「匿名」→「非公開」の置換を新版向けに戻した（2026-09-29・Q332）。
  //   新版化で「非表示の顧客名のラベル」の設定で足りると見て 8295c1b で削除したが、
  //   Judge.me の仕様で日本語（非ラテン文字）の名前はイニシャル形式に対応しておらず、
  //   ラベルではなく「匿名」に固定されて表示される（Judge.me サポート回答・2026-09-29）。
  //   表示形式を「匿名」にしても全件「匿名」になるため、設定では「非公開」にできない。
  const AUTHOR_NAME_SELECTORS = [
    '.jdgm-rev__author',          // 旧版
    '.jm-reviewer-info__name'     // 新版（Upgrade 後）
  ].join(', ');

  function handleReviewDisplayCommon() {
    // 「匿名」→「非公開」の置換
    // 新版は Vue が描画するので、要素ごと差し替えずテキストノードの値だけを書き換える
    // （textContent を代入すると Vue が保持しているノードが外れる）
    function replaceAuthorName(element) {
      if ((element.textContent || '').trim() !== '匿名') return;
      const textNode = Array.prototype.find.call(element.childNodes, function(n) {
        return n.nodeType === Node.TEXT_NODE && n.nodeValue.trim() === '匿名';
      });
      if (textNode) {
        textNode.nodeValue = textNode.nodeValue.replace('匿名', '非公開');
      } else {
        element.textContent = '非公開';
      }
      if (element.getAttribute('title') === '匿名') {
        element.setAttribute('title', '非公開');
      }
    }

    // 「お客様の声」→「REVIEW」の置換
    function replaceReviewTitle(element) {
      if ((element.textContent || '').trim() === 'お客様の声') {
        element.textContent = 'REVIEW';
      }
    }

    function processElements() {
      document.querySelectorAll(AUTHOR_NAME_SELECTORS).forEach(replaceAuthorName);
      document.querySelectorAll('.jdgm-rev-widg__title').forEach(replaceReviewTitle);
    }

    // 初回実行
    processElements();

    // MutationObserver を設定
    // 新版はページ送りでテキストだけが差し替わることがあるので characterData も見る。
    // 自分の書き換えでも発火するが、置換後は一致しないので再帰しない
    const observer = new MutationObserver(function() {
      processElements();
    });

    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
  }

  // -----------------------------
  // ドロップダウン要素削除（全レビューページ用）
  // -----------------------------
  function handleDropdownCleanup() {
    function removeDropdownOptions() {
      const dropdown = document.querySelector('.jdgm-sort-dropdown');
      if (dropdown) {
        const optionsToRemove = [
          'with-pictures',
          'pictures-first',
          'videos-first',
          'most-helpful'
        ];
        
        optionsToRemove.forEach(function(value) {
          const option = dropdown.querySelector('option[value="' + value + '"]');
          if (option) option.remove();
        });
      }
    }

    removeDropdownOptions();

    const observer = new MutationObserver(function() {
      removeDropdownOptions();
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  // -----------------------------
  // ページタイトル置換（全レビューページ専用）
  // -----------------------------
  function handlePageTitleReplacement() {
    function replaceTitle() {
      const titleElement = document.querySelector('h1.SectionHeader__Heading#custom-mp-ttl');
      if (titleElement && (titleElement.textContent || '').trim() === 'お客様の声') {
        titleElement.textContent = 'REVIEW';
      }
    }

    replaceTitle();

    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.type === 'childList' || mutation.type === 'characterData') {
          replaceTitle();
        }
      });
    });

    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
  }

    // -----------------------------
  // ストアレビュー終了のお知らせリンク（全レビューページ専用）
  // -----------------------------
  function handleStoreReviewNotice() {
    const NOTICE_ENABLED = false; // 不要になったら false に変更
    const NOTICE_ID = 'picollet-store-review-notice';
    const NOTICE_TEXT = 'ストアレビュー受付終了のお知らせ';
    const NOTICE_URL = '/blogs/information/%E3%82%B9%E3%83%88%E3%82%A2%E3%83%AC%E3%83%93%E3%83%A5%E3%83%BC%E5%8F%97%E4%BB%98%E7%B5%82%E4%BA%86%E3%81%AE%E3%81%8A%E7%9F%A5%E3%82%89%E3%81%9B';

    function upsertNotice() {
      const existingNotice = document.getElementById(NOTICE_ID);

      if (!NOTICE_ENABLED) {
        if (existingNotice) existingNotice.remove();
        return;
      }

      const actions = document.querySelector('.jdgm-rev-widg__actions');
      if (!actions || !actions.parentNode) return;

      let notice = existingNotice;

      if (!notice) {
        notice = document.createElement('div');
        notice.id = NOTICE_ID;
        notice.className = 'picollet-store-review-notice';
      }

      if (
        notice.getAttribute('data-notice-url') !== NOTICE_URL ||
        notice.getAttribute('data-notice-text') !== NOTICE_TEXT
      ) {
        const link = document.createElement('a');
        link.href = NOTICE_URL;
        link.textContent = NOTICE_TEXT;

        notice.textContent = '';
        notice.appendChild(link);
        notice.setAttribute('data-notice-url', NOTICE_URL);
        notice.setAttribute('data-notice-text', NOTICE_TEXT);
      }

      if (notice.parentNode !== actions.parentNode || notice.nextSibling !== actions) {
        actions.parentNode.insertBefore(notice, actions);
      }
    }

    upsertNotice();

    const observer = new MutationObserver(function() {
      upsertNotice();
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }
  
  // -----------------------------
  // all-reviews レビュー一覧bodyの表示補正
  // -----------------------------
  function handleAllReviewsBodyHeightFix() {
    let scheduled = false;
    const fallbackClass = 'picollet-jdgm-layout-fallback';
    const styleId = 'picollet-jdgm-layout-fallback-style';

    function ensureFallbackStyle() {
      if (document.getElementById(styleId)) return;

      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .jdgm-all-reviews__body.${fallbackClass} {
          height: auto !important;
          display: grid !important;
          grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
          gap: 16px !important;
          position: static !important;
        }

        .jdgm-all-reviews__body.${fallbackClass} .jdgm-rev {
          display: block !important;
          position: relative !important;
          left: auto !important;
          top: auto !important;
          width: auto !important;
          transform: none !important;
        }

        @media screen and (max-width: 749px) {
          .jdgm-all-reviews__body.${fallbackClass} {
            grid-template-columns: 1fr !important;
          }
        }
      `;

      document.head.appendChild(style);
    }

    function shouldUseFallback(body) {
      if (!body) return false;

      const reviews = body.querySelectorAll('.jdgm-rev');
      if (!reviews.length) return false;

      if (body.classList.contains(fallbackClass)) return false;

      const bodyHeight = body.getBoundingClientRect().height;

      return bodyHeight < 100;
    }

    function applyFallbackIfNeeded() {
      const bodies = Array.prototype.slice.call(
        document.querySelectorAll('.jdgm-all-reviews__body')
      );

      bodies.forEach(function(body) {
        if (shouldUseFallback(body)) {
          ensureFallbackStyle();
          body.classList.add(fallbackClass);
        }
      });
    }

    function scheduleFix() {
      if (scheduled) return;

      scheduled = true;

      requestAnimationFrame(function() {
        scheduled = false;
        applyFallbackIfNeeded();
      });
    }

    scheduleFix();

    [100, 300, 700, 1500, 3000].forEach(function(delay) {
      setTimeout(scheduleFix, delay);
    });

    window.addEventListener('resize', scheduleFix);

    document.addEventListener('change', function(event) {
      if (event.target && event.target.matches('.jdgm-sort-dropdown')) {
        setTimeout(scheduleFix, 300);
        setTimeout(scheduleFix, 1000);
      }
    }, true);

    const observer = new MutationObserver(function() {
      scheduleFix();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // -----------------------------
  // all-reviews 初期タブ状態の正規化
  // -----------------------------
  function normalizeAllReviewsInitialTabState() {
    let attempts = 0;
    let relayoutAttempts = 0;
    const maxAttempts = 30;
    const maxRelayoutAttempts = 3;
    const timeoutMs = 6500;
    const relayoutDelayMs = 180;
    const productTabSelector = '.jdgm-subtab__name[data-tabname="product-reviews"]';
    const storeTabSelector = '.jdgm-subtab__name[data-tabname="shop-reviews"]';
    const productBodySelector = '.jdgm-all-reviews__body';
    const storeBodySelector = '.jdgm-shop-reviews__body';
    let observer = null;
    let timeoutId = null;
    let relayoutTimerId = null;
    let relayoutInFlight = false;
    let hiddenWrapper = null;
    let previousVisibility = '';
    function stop() {
      if (observer) observer.disconnect();
      observer = null;
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = null;
      if (relayoutTimerId) clearTimeout(relayoutTimerId);
      relayoutTimerId = null;
      relayoutInFlight = false;
      if (hiddenWrapper) {
        hiddenWrapper.style.visibility = previousVisibility;
        hiddenWrapper = null;
        previousVisibility = '';
      }
    }

    function getState() {
      const productTab = document.querySelector(productTabSelector);
      const storeTab = document.querySelector(storeTabSelector);
      const productBody = document.querySelector(productBodySelector);
      const storeBody = document.querySelector(storeBodySelector);
      const firstReview = productBody ? productBody.querySelector('.jdgm-rev') : null;

      return {
        productTab: productTab,
        storeTab: storeTab,
        productBody: productBody,
        storeBody: storeBody,
        firstReview: firstReview
      };
    }

    function getSharedWrapper(productBody, storeBody) {
      if (!productBody || !storeBody) return null;

      const ancestors = [];
      let node = productBody;
      while (node) {
        ancestors.push(node);
        node = node.parentElement;
      }

      node = storeBody;
      while (node) {
        if (ancestors.indexOf(node) !== -1) return node;
        node = node.parentElement;
      }

      return productBody.parentElement || null;
    }

    function isProductTabActive(productTab) {
      return !!(productTab && productTab.classList.contains('jdgm--active'));
    }

    function isStoreTabActive(storeTab) {
      return !!(storeTab && storeTab.classList.contains('jdgm--active'));
    }

    function hasProductContent(productBody) {
      if (!productBody) return false;
      return productBody.children.length > 0 && (productBody.innerHTML || '').length > 0;
    }

    function isStoreBodyVisible(storeBody) {
      if (!storeBody) return false;
      const style = window.getComputedStyle(storeBody);
      return style.display !== 'none' && style.visibility !== 'hidden';
    }

    function hasInitializedProductLayout(productBody, firstReview) {
      if (!productBody || !firstReview) return false;

      const bodyStyle = productBody.getAttribute('style') || '';
      const reviewStyle = window.getComputedStyle(firstReview);

      return (
        /position:\s*relative/i.test(bodyStyle) &&
        /height:\s*[^;]+/i.test(bodyStyle) &&
        reviewStyle.position === 'absolute'
      );
    }

    function isAbnormalState(state) {
      if (!state.productTab || !state.storeTab || !state.productBody || !state.storeBody) {
        return false;
      }

      if (!hasProductContent(state.productBody)) return false;

      const productLayoutReady = hasInitializedProductLayout(state.productBody, state.firstReview);
      if (!productLayoutReady) {
        return true;
      }

      if (isStoreTabActive(state.storeTab)) {
        return true;
      }

      if (!isProductTabActive(state.productTab)) {
        return true;
      }

      if (isStoreBodyVisible(state.storeBody)) {
        return true;
      }

      return false;
    }

    function isNormalized(state) {
      if (!state.productTab || !state.storeTab || !state.productBody || !state.storeBody) {
        return false;
      }

      if (!isProductTabActive(state.productTab)) return false;
      if (isStoreTabActive(state.storeTab)) return false;

      return hasInitializedProductLayout(state.productBody, state.firstReview);
    }

    function hideWrapper(state) {
      if (hiddenWrapper) return;
      hiddenWrapper = getSharedWrapper(state.productBody, state.storeBody);
      if (!hiddenWrapper) return;
      previousVisibility = hiddenWrapper.style.visibility || '';
      hiddenWrapper.style.visibility = 'hidden';
    }

    function queueProductTabRestore(restoreAttempts) {
      requestAnimationFrame(function() {
        requestAnimationFrame(function() {
          relayoutTimerId = setTimeout(function() {
            relayoutTimerId = null;
            const nextState = getState();
            if (!nextState.productTab) {
              relayoutInFlight = false;
              return;
            }

            nextState.productTab.click();

            relayoutTimerId = setTimeout(function() {
              relayoutTimerId = null;
              const restoredState = getState();
              const productRestored =
                restoredState.productTab &&
                isProductTabActive(restoredState.productTab) &&
                !isStoreTabActive(restoredState.storeTab) &&
                hasInitializedProductLayout(restoredState.productBody, restoredState.firstReview);

              if (productRestored) {
                relayoutInFlight = false;
                return;
              }

              if (restoreAttempts < maxRelayoutAttempts) {
                queueProductTabRestore(restoreAttempts + 1);
                return;
              }

              relayoutInFlight = false;
            }, relayoutDelayMs);
          }, relayoutDelayMs);
        });
      });
    }

    function triggerRelayout(state) {
      if (relayoutInFlight) return;
      if (relayoutAttempts >= maxRelayoutAttempts) return;

      relayoutAttempts += 1;
      relayoutInFlight = true;
      hideWrapper(state);

      state.storeTab.click();
      queueProductTabRestore(1);
    }

    function tryNormalize() {
      attempts += 1;
      const state = getState();

      if (isNormalized(state)) {
        stop();
        return;
      }

      if (isAbnormalState(state)) {
        triggerRelayout(state);
      }

      if (attempts >= maxAttempts) {
        stop();
      }
    }

    observer = new MutationObserver(function() {
      tryNormalize();
    });

    observer.observe(document.body, { childList: true, subtree: true, attributes: true });
    timeoutId = setTimeout(function() {
      stop();
    }, timeoutMs);
    tryNormalize();
  }

  // -----------------------------
  // 初期化
  // -----------------------------
  function initAll() {
    if (initialized) {
      debugLog('initAll already called, skipping');
      return;
    }
    initialized = true;
    debugLog('initAll called');
    
    // 共通のレビュー表示処理（お客様の声→REVIEW）
    handleReviewDisplayCommon();
    
    // フォーム処理とバリエーション表示
    handleJudgeMeForm();
    handleReviewDisplay();
    
    // 全レビューページの場合のみ追加処理
    if (window.location.pathname.includes('/pages/all-reviews')) {
      handleDropdownCleanup();
      handlePageTitleReplacement();
      // normalizeAllReviewsInitialTabState();
      handleStoreReviewNotice();
      handleAllReviewsBodyHeightFix();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      debugLog('DOMContentLoaded');
      initAll();
    });
  } else {
    initAll();
  }

  if (window.jdgm) {
    debugLog('window.jdgm already present');
    initAll();
  } else {
    window.addEventListener('jdgm-script-loaded', function() {
      debugLog('jdgm-script-loaded');
      initAll();
    });
  }

  // 念のため遅延実行
  setTimeout(function() {
    debugLog('delayed init');
    initAll();
  }, 2000);

})();
